import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../hooks/redux/AppState';
import axios from 'ps-components/lib/apiClient';
import cn from 'classnames';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import SubLabelList from 'ps-components/SubLabelList/SubLabelList';
import MarketplaceMatchesNew from './MarketplaceMatchesNew';
import { marketplaceMatchesGridActions } from '../MarketplaceMatchesGrid/MarketplaceMatchesGridCreate';
import { marketplaceMatchesSlideoutSelector } from './selectors';
import * as actions from '../MarketplaceMatchesActions';
import addAlert from 'ps-components/AlertToast/addAlert';
import '../MarketplaceMatchesSlideouts/MarketplaceMatchesSlideout.scss';
import '../../MarketPricingContainer.scss'


interface Props {
  selectedJobIds: number[]
  startingJobIndex?: number
  hideSlideout: () => void
  dontRefetch?: boolean
}

const MarketplaceMatchesSlideout: FC<Props> = ({ selectedJobIds, startingJobIndex = 0, hideSlideout, dontRefetch = false }) => {
  const dispatch = useAppDispatch()
  const [fetchingJobDetails, setFetchingJobDetails] = useState<boolean>(false)
  const { session, slideout } = useSelector(marketplaceMatchesSlideoutSelector)
  const { job_code_key_text } = session.dynamic_columns
  const { useJobCodeKey } = session.organization
  const { currentJobIndex, jobs } = slideout

  const getJobDetails = async () => {
    setFetchingJobDetails(true)
    try {
      const response = await (axios as any).apiPost('/api/market-pricing/jobs/jobDetails', { jobIds: selectedJobIds })
      const { jobs } = response.data
      dispatch(actions.setSlideoutJobs({ selectedJobIds, startingJobIndex, jobs }))
    } catch (err) {
      console.error(err)
      dispatch(hideSlideout)
      addAlert('There was an error getting job details', 'danger');
    } finally {
      setFetchingJobDetails(false)
    }
  }

  useEffect(() => {
    if (!dontRefetch) {
      dispatch(marketplaceMatchesGridActions.reset())
      dispatch(actions.changeSlideoutJob())
      dispatch(actions.resetMarketplaceMatches())
      dispatch(actions.toggleActiveSurveys(true))
      dispatch(actions.getPreferences())
      dispatch(actions.getGeographies())
      dispatch(actions.getIndustries())
      getJobDetails()
    }
  }, [selectedJobIds])

  const handleJobChange = (prevOrNext: 'previous' | 'next') => {
    if ((currentJobIndex === 0 && prevOrNext === 'previous') || (currentJobIndex === selectedJobIds.length - 1 && prevOrNext === 'next')) return
    dispatch(marketplaceMatchesGridActions.reset())
    dispatch(actions.resetMarketplaceMatches())
    dispatch(actions.changeSlideoutJob(prevOrNext))
  }

  if (fetchingJobDetails) return null

  const currentJob = jobs[currentJobIndex]

  const subLabelList = [
    { label: 'Job Code', text: currentJob?.code },
    { label: 'Currency', text: currentJob?.currency },
    { label: 'Selected Benchmark Year', text: session?.benchmarkYear }
  ];

  if (useJobCodeKey) subLabelList.splice(1, 0, { label: job_code_key_text, text: currentJob?.codeKey === ' ' ? 'Not Set' : currentJob?.codeKey })

  return (
    <div className={cn('mpm-slideout', { slotted: status !== 'priced' })} id="mpm-slideout">
      <SlideoutHeader title="Add Marketplace Matches" doClose={() => dispatch(hideSlideout)} />
      <div className="market-pricing-title__container">
        <div className='market-pricing__title'>
          <div className="market-pricing__title-text">
            {currentJob?.title}
          </div>
          <SubLabelList subLabels={subLabelList} />
        </div>
        {selectedJobIds.length > 1 &&
          <div className="marketprice-matches-header__page-navigation">
            <a className={`${currentJobIndex === 0 ? 'nav-link-disabled' : ''}`} onClick={() => handleJobChange('previous')}>
              <i className="icon-left-open" />
              Previous Job
            </a>
            <p className="marketprice-matches-header__page-navigation-divider">|</p>
            <a className={`${currentJobIndex === selectedJobIds.length - 1 ? 'nav-link-disabled' : ''}`} onClick={() => handleJobChange('next')}>
              Next Job
              <i className="icon-right-open" />
            </a>
          </div>
        }
      </div>
      <MarketplaceMatchesNew />

    </div>
  )
}

export default MarketplaceMatchesSlideout
